import React, { useState, useEffect, useCallback } from "react";
import "./Budget.css";
import { Search, Filter, Plus, ChevronLeft, ChevronRight, MoreVertical, ChevronDown, SlidersHorizontal, ArrowUpNarrowWide } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { apiCall } from "../../services/api-service.js";
import editIcon from "../../Assets/svg/edit.svg";
import deleteIcon from "../../Assets/svg/delete.svg";
import lar from "../../Assets/svg/lar.svg";
import rar from "../../Assets/svg/rar.svg";
import DeleteModal from "../common/DeleteModal";


const BudgetList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [itemOffset, setItemOffset] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState("Created Date (Newest)");
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);


  const getListData = async () => {
    try {
      const sortMap = {
        "Budget (A-Z)": "budget_asc",
        "Budget (Z-A)": "budget_desc",
        "Created Date (Newest)": "id_desc",
        "Created Date (Oldest)": "id_asc",
      };

      const response = await apiCall("get", "budget", null, {
        params: {
          limit: itemsPerPage,
          skip: itemOffset,
          search: searchTerm,
          status: status,
          sort_by: sortMap[sortBy] || "id_desc"
        },
      });

      if (response?.data?.success === true) {
        const total = response.data.total_rec ?? 0;
        setTotalCount(total);
        // Force calculation on frontend to be safe
        setPageCount(Math.ceil(total / itemsPerPage));
        setBudgets(response.data.budgets ?? []);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
    }
  };

  useEffect(() => {
    getListData();
  }, [itemsPerPage, itemOffset, searchTerm, status, sortBy]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".custom-dropdown-wrapper")) {
        setShowStatusDropdown(false);
        setShowSortDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePageClick = useCallback(
    (event) => {
      const newOffset = event.selected * itemsPerPage;
      setItemOffset(newOffset);
    },
    [itemsPerPage]
  );

  const getTypeStyle = (type) => {
    const t = (type || "").toUpperCase();
    if (t === "SUPPORT") {
      return { backgroundColor: "#E8DBFF", color: "#6929E2", border: "1px solid #D5BEFF" };
    }
    if (t === "DEVELOPMENT") {
      return { backgroundColor: "#E6FFF1", color: "#20824A", border: "1px solid #BBDAC8" };
    }
    return { backgroundColor: "#F3F4F6", color: "#6B7280", border: "1px solid rgba(107, 114, 128, 0.1)" };
  };

  const getStatusClass = (status) => {
    return (status || "").toLowerCase();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await apiCall("delete", `budget/${deleteId}`);
      if (response?.data?.success === true) {
        getListData();
      } else {
        console.error(response?.data?.message || "Error deleting budget");
      }
    } catch (error) {
      console.error("Error deleting budget:", error);
    } finally {
      setIsOpenDelete(false);
      setDeleteId(null);
    }
  };


  return (
    <div className="budget-container">
      <div className="budget-header">
        <div className="header-left">
          <h1>Budget</h1>
          <p>Manage your budgets and create new budgets here</p>
        </div>
        <button className="add-budget-btn" onClick={() => navigate("/budget/add")}>
          <Plus size={18} /> Add budget
        </button>
      </div>

      <div className="budget-filters-row">
        <div className="total-budgets">Total budgets : {totalCount}</div>
        <div className="filters-right">
          <div className="search-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search budgets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {/* <button className="filter-icon-btn">
            <SlidersHorizontal className="purple-icon" size={18} />
          </button> */}
          <div className="custom-dropdown-wrapper">
            <div
              className="custom-dropdown-trigger"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <span className="dropdown-text">
                {status ? status.charAt(0).toUpperCase() + status.slice(1) : "All Status"}
              </span>
              <ChevronDown size={18} className={`purple-icon dropdown-arrow ${showStatusDropdown ? "open" : ""}`} />
            </div>

            {showStatusDropdown && (
              <div className="custom-dropdown-menu">
                <div
                  className={`dropdown-item ${status === "" ? "selected" : ""}`}
                  onClick={() => {
                    setStatus("");
                    setItemOffset(0);
                    setShowStatusDropdown(false);
                  }}
                >
                  All Status
                </div>
                <div
                  className={`dropdown-item ${status === "draft" ? "selected" : ""}`}
                  onClick={() => {
                    setStatus("draft");
                    setItemOffset(0);
                    setShowStatusDropdown(false);
                  }}
                >
                  Draft
                </div>
                <div
                  className={`dropdown-item ${status === "active" ? "selected" : ""}`}
                  onClick={() => {
                    setStatus("active");
                    setItemOffset(0);
                    setShowStatusDropdown(false);
                  }}
                >
                  Active
                </div>
                <div
                  className={`dropdown-item ${status === "closed" ? "selected" : ""}`}
                  onClick={() => {
                    setStatus("closed");
                    setItemOffset(0);
                    setShowStatusDropdown(false);
                  }}
                >
                  Closed
                </div>
              </div>
            )}
          </div>
          <div className="custom-dropdown-wrapper">
            <button
              className="custom-dropdown-btn sort-btn"
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              <div className="sort-btn-content">
                <ArrowUpNarrowWide size={18} className="purple-icon" />
                <span className="dropdown-text">
                  {sortBy === "Created Date (Newest)" || sortBy === "Created Date (Oldest)" ? (
                    <>Default <span className="gray-text">({sortBy.includes("Newest") ? "Created" : "Oldest"})</span></>
                  ) : (
                    sortBy
                  )}
                </span>
              </div>
              <ChevronDown size={18} className={`purple-icon dropdown-arrow ${showSortDropdown ? "open" : ""}`} />
            </button>

            {showSortDropdown && (
              <div className="custom-dropdown-menu sort-menu">
                <div
                  className={`dropdown-item ${sortBy === "Budget (A-Z)" ? "selected" : ""}`}
                  onClick={() => {
                    setSortBy("Budget (A-Z)");
                    setShowSortDropdown(false);
                  }}
                >
                  Budget (A-Z)
                </div>
                <div
                  className={`dropdown-item ${sortBy === "Budget (Z-A)" ? "selected" : ""}`}
                  onClick={() => {
                    setSortBy("Budget (Z-A)");
                    setShowSortDropdown(false);
                  }}
                >
                  Budget (Z-A)
                </div>
                <div
                  className={`dropdown-item ${sortBy === "Created Date (Newest)" ? "selected" : ""}`}
                  onClick={() => {
                    setSortBy("Created Date (Newest)");
                    setShowSortDropdown(false);
                  }}
                >
                  Created Date (Newest)
                </div>
                <div
                  className={`dropdown-item ${sortBy === "Created Date (Oldest)" ? "selected" : ""}`}
                  onClick={() => {
                    setSortBy("Created Date (Oldest)");
                    setShowSortDropdown(false);
                  }}
                >
                  Created Date (Oldest)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="budget-table-container">
        <table className="budget-table mt-3">
          <thead>
            <tr>
              <th>Code</th>
              <th>Budget</th>
              <th>Project</th>
              <th>Type</th>
              <th>Period</th>
              <th>Pricing Type</th>
              <th>Period Type</th>
              <th>Timing Type</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Duration</th>
              <th className="actions-header"></th>
            </tr>
          </thead>
          <tbody>
            {budgets.map((budget, index) => (
              <tr
                key={budget.id || index}
                className="budget-row"
                onClick={() => navigate("/budget/view", { state: { id: budget.id } })}
                style={{ cursor: "pointer" }}
              >
                <td className="code-cell">{budget.budget_code || budget.code}</td>
                <td className="budget-cell">{budget.budget || budget.budget_title || budget.name}</td>
                <td className="project-cell">{budget.project_name || budget.project?.name || budget.project}</td>
                <td>
                  <span className="badge type-badge" style={getTypeStyle((budget.type || budget.type_name || "").split(',')[0].trim().toUpperCase())}>
                    {(budget.type || budget.type_name || "").split(',')[0].trim().toUpperCase()}
                  </span>
                </td>
                <td className="period-cell no-wrap">{budget.period || `${budget.start_date || ""} ${budget.end_date ? "- " + budget.end_date : ""}`}</td>
                <td>
                  <span className="badge pricing-badge">
                    {budget.pricing_type?.replace('_', ' ')?.toUpperCase() || budget.pricingType}
                  </span>
                </td>
                <td>
                  <span className="badge period-badge">
                    {budget.period_type?.toUpperCase() || budget.periodType}
                  </span>
                </td>
                <td>
                  <span className="badge timing-badge">
                    {budget.timing_type?.toUpperCase() || budget.timingType}
                  </span>
                </td>
                <td>
                  <div className={`status-indicator ${getStatusClass(budget.status || "")}`}>
                    <span className="status-dot"></span>
                    <span className="status-text">{budget.status}</span>
                  </div>
                </td>
                <td className="cost-cell">{budget.total_cost || budget.cost}</td>
                <td className="duration-cell">{budget.duration}</td>
                <td className="actions-cell">
                  <div className="row-actions">
                    <button
                      className="action-btn edit-btn"
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/budget/edit/${budget.id}`, { state: { from: "list" } });
                      }}

                    >
                      <img src={editIcon} alt="Edit" />
                    </button>
                     <button
                      className="action-btn delete-btn"
                      title="Delete"
                      style={{
                        cursor: ["closed", "active"].includes(budget.status?.toLowerCase()) ? "not-allowed" : "pointer",
                        opacity: ["closed", "active"].includes(budget.status?.toLowerCase()) ? 0.5 : 1
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const status = budget.status?.toLowerCase();
                        if (["closed", "active"].includes(status)) {
                          return;
                        }
                        setDeleteId(budget.id);
                        setIsOpenDelete(true);
                      }}

                    >
                      <img src={deleteIcon} alt="Delete" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="budget-pagination d-flex justify-content-between align-items-center">
        <div className="records-per-page">
          Records per page :
          <div className="records-select-wrapper">
            <select
              className="records-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setItemOffset(0);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <ChevronDown className="select-icon" size={14} />
          </div>
        </div>
        <div className="pagination-controls">
          <ReactPaginate
            previousLabel={<ChevronLeft size={18} />}
            nextLabel={<ChevronRight size={18} />}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            containerClassName={"pagination"}
            onPageChange={handlePageClick}
            activeClassName={"active"}
            forcePage={pageCount > 0 ? Math.floor(itemOffset / itemsPerPage) : 0}
          />
        </div>
      </div>

      {/* Floating Action Button (Remote work) - Already in App.js but matching style if needed */}
      <DeleteModal
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={handleDelete}
        title="Delete Budget"
        message="Are you sure you want to delete this budget?"
      />
    </div>

  );
};

export default BudgetList;
